import { skipToken } from '@reduxjs/toolkit/query';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import GridItemList, { infoLabel, Row as GridItemRow } from 'components/GridItemList';
import { InvoiceAutoGenerationScheduleValue } from 'components/InvoiceAutoGenerationScheduleInfo';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useModal } from 'components/modals/useModal';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import {
    MATTER_PRICELIST_EDITOR_MODAL_STYLE,
    MatterPricelistEditorModal,
} from 'pages/pricelists/modal/MatterPricelistEditorModal';
import React, { use, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useFindMatterRatesQuery } from 'store/api/matters';
import { useFindPricelistsByClientQuery } from 'store/api/pricelists';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { MatterDto, MatterPartyRoleType, MatterStateType, PricingLimitationType } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { PricelistCardEntry, PricingKind, ScopeType } from 'types/pricelists';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { getInvoiceScheduleKind } from 'utils/invoiceAutoGenerationSchedule';
import { PartyNameParty } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { EditPricelistCustomNameModal } from '../../pricelists/modal/EditPricelistCustomNameModal';
import { CUSTOMIZE_INVOICE_PERMISSIONS } from '../form-sections/InvoiceSpecFields';
import { MatterSection } from '../form-sections/MatterSection';
import { PayerShareList } from '../PayerShareList';
import { ENTITY_LINK_CLASS, getLinkChips, getLinks } from '../util.tsx';
import { CaseEmployeePricing } from './CaseEmployeePricing';
import { clientPricelistsArg, findPricelistForMatter, MatterPricelistCard } from './MatterPricelistCard';

interface Props {
    classifiers: Record<string, ClassifierData>;
    matter: MatterDto;
    refetchMatter: () => void;
}

const PricingView: React.FC<Props> = ({ classifiers, matter, refetchMatter }) => {
    // The new design's cards share their form's grid: a row per field, in the form's order, under its label.
    const isNew = useIsNewDesign();
    const missingFields = use(DraftActionsContext)?.draft?.missingFields;
    const isPricingNonBillable = matter.pricingLimitationTypeCode === PricingLimitationType.NON_BILLABLE;
    const isPricingCapped = matter.pricingLimitationTypeCode === PricingLimitationType.HOURLY_CAPPED;
    const isPricingFixed = matter.pricingLimitationTypeCode === PricingLimitationType.FIXED_ONETIME;
    const isPricingRecurringFixed = matter.pricingLimitationTypeCode === PricingLimitationType.FIXED_RECURRING;
    const { openModal, closeModal } = useModal();
    // Every list of the case: the card reads the in-force list's stored figures, the popover shows them all.
    const { data: pricelists } = useFindMatterRatesQuery(matter.id ? String(matter.id) : skipToken);
    const cardLists = useMemo<PricelistCardEntry[]>(
        () => (pricelists ?? []).map((list) => ({ ...list, key: `list-${list.id}` })),
        [pricelists]
    );
    const pricingKind = matter.pricingKind ?? pricelists?.[0]?.pricingKind ?? PricingKind.ROLE;
    const isPricingLockedByState =
        matter.matterStateTypeCode === MatterStateType.OFFER_LOST ||
        matter.matterStateTypeCode === MatterStateType.OFFER_WON ||
        matter.matterStateTypeCode === MatterStateType.COMPLETED;
    const caseTeam = useMemo(() => {
        const people = new Map<number, NamedEntityMinDto>();
        [
            ...(matter.employees ?? []).map((link) => link.employee),
            ...(matter.paymentNotificationReceivers ?? []).map((link) => link.employee),
            matter.responsibleEmployee,
            matter.supervisedByEmployee,
        ]
            .filter((person): person is NamedEntityMinDto => !!person)
            .forEach((person) => people.set(person.id, person));
        return [...people.values()];
    }, [
        matter.employees,
        matter.paymentNotificationReceivers,
        matter.responsibleEmployee,
        matter.supervisedByEmployee,
    ]);
    const isInvoicingInfoEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_INFO_ENABLED);
    const isCasePaymentCurrencyEnabled = useIsFeatureEnabled(FeatureFlag.CASE_PAYMENT_CURRENCY_FIELD);
    const isPaymentCurrencyDifferent =
        !!matter.paymentCurrencyTypeCode && matter.paymentCurrencyTypeCode !== matter.pricelistCurrencyTypeCode;
    const isLocalizedFieldsEnabled = useIsFeatureEnabled([
        FeatureFlag.LOCALIZED_FIELDS,
        FeatureFlag.LOCALIZED_FIELDS_CASES,
    ]);
    const isAccountingRevenueTypeEnabled = useIsFeatureEnabled(FeatureFlag.MATTER_ACCOUNTING_REVENUE_TYPE);
    const isSharedPaymentEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_SHARED_PAYMENT_FUNCTIONALITY);
    // With it on the case form has no field for keeping the non-billable hours.
    const hidesPreserveNonBillableField = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    // The form has a Customise button under the invoice template for those who may use it.
    const isInvoiceCustomizationEnabled = useIsFeatureEnabled(FeatureFlag.USE_CUSTOMIZED_INVOICE_TEMPLATE);
    const canCustomizeInvoice = useHasPermissions(CUSTOMIZE_INVOICE_PERMISSIONS);
    const hasCustomizeButton = isNew && matter.isEditable && isInvoiceCustomizationEnabled && canCustomizeInvoice;

    const { data: clientPricelists } = useFindPricelistsByClientQuery(clientPricelistsArg(matter));
    const matchingPricelist = findPricelistForMatter(clientPricelists, matter);

    const openEditHourlyRatesModal = useCallback(
        (matter: MatterDto): void => {
            openModal(
                <MatterPricelistEditorModal
                    mode="page"
                    matterId={matter.id}
                    lists={pricelists ?? []}
                    clientParty={matter.clientParty}
                    referencedPricelist={{
                        id: matter.pricelist?.id ?? null,
                        pricingHash: matter.pricingHash,
                    }}
                    pricingKind={pricingKind}
                    followsPricelist={matter.followsPricelist ?? pricelists?.[0]?.followsPricelist ?? null}
                    canSwitchKind={matter.isEditable}
                    caseTeam={caseTeam}
                    openedDate={matter.openedDate ?? null}
                    onClose={closeModal}
                    onSaved={refetchMatter}
                    classifiers={classifiers}
                    currencyTypeCode={matter.pricelistCurrencyTypeCode}
                    serviceAgreementContractId={matter.serviceAgreementContract?.id}
                />,
                closeModal,
                MATTER_PRICELIST_EDITOR_MODAL_STYLE
            );
        },
        [openModal, closeModal, refetchMatter, classifiers, pricelists, caseTeam, pricingKind]
    );

    // Only a role-priced case's own (matter-scoped) list carries a custom name: model and contract lists are
    // renamed through their own edit flow, and the server refuses one on any employee-priced case.
    const isCaseBasedPricelist =
        matchingPricelist?.scopeTypeCode === ScopeType.MATTER && pricingKind !== PricingKind.EMPLOYEE;

    const openEditCustomNameModal = useCallback((): void => {
        openModal(
            <EditPricelistCustomNameModal
                matterId={matter.id}
                currentCustomName={matchingPricelist?.customName}
                onClose={closeModal}
            />,
            closeModal,
            { width: 480 }
        );
    }, [openModal, closeModal, matter.id, matchingPricelist?.customName]);

    const currencyTypeCode = matter.pricelistCurrencyTypeCode;
    const pricelistName = matchingPricelist?.name ?? '';

    const getPricingData = useCallback((): GridItemRow[] => {
        const pricelistCard = (
            <MatterPricelistCard
                name={pricelistName}
                oneLineName={isNew}
                currencyTypeCode={currencyTypeCode}
                lists={cardLists}
                openedDate={matter.openedDate ?? null}
                classifiers={classifiers}
                pricingKind={pricingKind}
            />
        );
        // An employee-priced case shows its people and its link in the row, with the actions that change them.
        const employeePricingRow: GridItemRow = {
            content: (
                <CaseEmployeePricing
                    matter={matter}
                    lists={pricelists ?? []}
                    canEdit={matter.isEditable && !isPricingLockedByState}
                    canMakeOwnCopy={matter.isEditable}
                    onEdit={() => openEditHourlyRatesModal(matter)}
                    onSaved={refetchMatter}
                />
            ),
            label: t('matters.pricelist'),
            wide: 'always',
        };
        const schedule = (
            <InvoiceAutoGenerationScheduleValue invoicingIntervalCode={matter.invoicingIntervalTypeCode} />
        );
        const rows: GridItemRow[] = [
            {
                content: getClassifierText(classifiers['CURRENCY'], matter.pricelistCurrencyTypeCode) || '-',
                label: t('currency'),
            },
            pricingKind === PricingKind.EMPLOYEE && pricelists
                ? employeePricingRow
                : {
                      // The card's line and the buttons are 24px: in the new design they sit on the cell's 20px line,
                      // and the list's name keeps to one, so the row is as tall as its field.
                      content:
                          cardLists.length === 0 ? (
                              ''
                          ) : isNew ? (
                              <div className="-my-0.5">{pricelistCard}</div>
                          ) : (
                              pricelistCard
                          ),
                      label: t('matters.pricelist'),
                      button: (
                          <div className={cn('flex gap-2', isNew && '-my-0.5')}>
                              {isCaseBasedPricelist ? (
                                  <PermissionButton
                                      hasAuthority={matter.isEditable}
                                      variant="outlined"
                                      size="extraSmall"
                                      onClick={openEditCustomNameModal}
                                  >
                                      <FormattedMessage id="matters.pricelist-custom-name.action" />
                                  </PermissionButton>
                              ) : null}
                              <PermissionButton
                                  hasAuthority={matter.isEditable}
                                  variant="outlined"
                                  size="extraSmall"
                                  disabled={!pricelists || isPricingLockedByState}
                                  onClick={() => openEditHourlyRatesModal(matter)}
                              >
                                  <FormattedMessage id="edit" />
                              </PermissionButton>
                          </div>
                      ),
                  },
            {
                content: isPaymentCurrencyDifferent ? t('yes') : t('no'),
                label: isNew
                    ? infoLabel(
                          'matters.use-different-payment-currency',
                          'matters.tooltips.use-different-payment-currency',
                          // No gap: the field's label has its line up to the icon, so both wrap at one width.
                          'justify-between'
                      )
                    : t('matters.use-different-payment-currency'),
                hidden: !isCasePaymentCurrencyEnabled,
            },
            {
                content: getClassifierText(classifiers['CURRENCY'], matter.paymentCurrencyTypeCode) || '-',
                label: t('payment-currency'),
                hidden: !(isCasePaymentCurrencyEnabled && isPaymentCurrencyDifferent),
            },
            {
                content:
                    getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], matter.pricingLimitationTypeCode) || '-',
                label: t('matters.fee-arrangement'),
            },
        ];

        if (isPricingNonBillable) {
            rows.push({
                content: matter.preserveNonBillableHours ? t('yes') : t('no'),
                label: isNew
                    ? infoLabel(
                          'matters.preserve-non-billable-hours',
                          'matters.tooltips.preserve-non-billable-hours',
                          'justify-between'
                      )
                    : t('matters.preserve-non-billable-hours'),
                readOnly: hidesPreserveNonBillableField,
            });
        }

        if (isPricingCapped) {
            rows.push(
                {
                    content: matter.capLimit || '-',
                    label: t('matters.cap-limit'),
                },
                {
                    content: getClassifierText(classifiers['CAP_UNIT_TYPE'], matter.capUnitTypeCode) || '-',
                    label: t('matters.cap-unit'),
                }
            );
        }

        if (isPricingFixed) {
            rows.push({
                content: matter.fixedFee || '-',
                label: t(isNew ? 'matters.fixed-fee-amount' : 'matters.fixed-fee'),
                formLabels: [t('matters.fixed-fee-amount')],
            });
        }

        if (isPricingRecurringFixed) {
            rows.push({
                content: matter.recurringFixedFee || '-',
                label: t(isNew ? 'matters.recurring-fee-amount' : 'matters.recurring-fee'),
                formLabels: [t('matters.recurring-fee-amount')],
            });
        }

        rows.push({
            content: getClassifierText(classifiers['INVOICING_INTERVAL_TYPE'], matter.invoicingIntervalTypeCode) || '-',
            label: t('matters.invoicing-interval'),
        });

        // Tenant-wide autogeneration schedule matching the invoicing interval class (read-only)
        const scheduleKind = getInvoiceScheduleKind(matter.invoicingIntervalTypeCode);
        if (scheduleKind) {
            rows.push({
                // As wide as the cell in the new design, as the form's read-only field has the value.
                content: isNew ? <div className="-mr-[11px]">{schedule}</div> : schedule,
                label: t(
                    scheduleKind === 'date'
                        ? 'financial-conditions.interval-date'
                        : 'financial-conditions.interval-weekday'
                ),
            });
        }

        rows.push(
            {
                content: matter.discountPct || '-',
                label: t('matters.discount'),
            },
            {
                content: <FormattedDate value={matter.discountExpiryDate} />,
                label: t('matters.discount-expiry'),
            },
            {
                content: matter.isCostIncluded ? t('yes') : t('no'),
                label: t('matters.costs-included'),
            },
            {
                content: matter.billingNotes || '-',
                label: isNew
                    ? infoLabel('matters.billing-notes', 'matters.billing-notes-tooltip')
                    : t('matters.billing-notes'),
                multiline: true,
            }
        );

        if (isInvoicingInfoEnabled) {
            rows.push({
                content: matter.invoicingInfo || '-',
                label: isNew
                    ? infoLabel('matters.invoicing-info-internal', 'matters.invoicing-info-tooltip')
                    : t('matters.invoicing-info-internal'),
                multiline: true,
            });
        }

        return rows;
    }, [
        matter,
        classifiers,
        isNew,
        hidesPreserveNonBillableField,
        isInvoicingInfoEnabled,
        isCasePaymentCurrencyEnabled,
        isPaymentCurrencyDifferent,
        isPricingNonBillable,
        currencyTypeCode,
        isPricingCapped,
        isPricingFixed,
        isPricingRecurringFixed,
        openEditHourlyRatesModal,
        isCaseBasedPricelist,
        openEditCustomNameModal,
        pricelistName,
        cardLists,
        pricelists,
        pricingKind,
        isPricingLockedByState,
        refetchMatter,
    ]);

    const getBillingData = useCallback((): GridItemRow[] => {
        // One line in the new design, as a contact's select shows it.
        const contact = (party: PartyNameParty | null | undefined, showEmail = false) => {
            if (!party) {
                return '-';
            }
            const name = (
                <PartyName party={party} isLink variant="readonly-other" showEmail={showEmail} singleLine={isNew} />
            );
            return isNew ? <EllipsisTooltip>{name}</EllipsisTooltip> : name;
        };
        const secondaryRecipients = (matter.parties || [])
            .filter(
                ({ matterPartyRoleTypeCode }) => matterPartyRoleTypeCode === MatterPartyRoleType.SECONDARY_RECIPIENT
            )
            .map(({ party }) => party);
        return [
            {
                content: getClassifierText(classifiers['LANGUAGE'], matter.timecardLanguageCode) || '-',
                label: t('matters.timecard-language'),
            },
            {
                content: matter.invoicedByOffice ? (
                    <RecordLink
                        className={ENTITY_LINK_CLASS}
                        to={`/office/${matter.invoicedByOffice.id}`}
                        label={matter.invoicedByOffice.name}
                    />
                ) : (
                    '-'
                ),
                label: t('matters.invoiced-by-office'),
            },
            {
                // The recipient fields show the e-mail after the name, and so do the new design's rows.
                content: contact(matter.primaryRecipientParty, isNew),
                label: isNew
                    ? infoLabel('matters.primary-recipient', 'matters.tooltips.primary-recipient')
                    : t('matters.primary-recipient'),
            },
            {
                content: matter.primaryRecipientAdditionalEmails || '-',
                label: isNew
                    ? infoLabel('matters.primary-recipient-emails', 'matters.tooltips.primary-recipient-emails')
                    : t('matters.primary-recipient-emails'),
                // As its text area shows it. Classic's row is one run of text.
                multiline: isNew,
            },
            {
                content: secondaryRecipients.length > 0 ? getLinks('contact', secondaryRecipients) : '-',
                label: isNew
                    ? infoLabel('matters.secondary-recipient', 'matters.tooltips.secondary-recipient')
                    : t('matters.secondary-recipient'),
                chips: getLinkChips('contact', secondaryRecipients, true),
            },
            {
                content: matter.secondaryRecipientAdditionalEmails || '-',
                label: isNew
                    ? infoLabel('matters.secondary-recipient-emails', 'matters.tooltips.secondary-recipient-emails')
                    : t('matters.secondary-recipient-emails'),
                multiline: isNew,
            },
            {
                content: matter.matterClientReference || '-',
                label: t('matters.client-reference'),
            },
            {
                content: matter.invoiceMatterName || '-',
                label: t('matters.name-on-invoice'),
            },
            ...(isLocalizedFieldsEnabled
                ? [
                      {
                          content: matter.invoiceMatterNameLocalLanguage || '-',
                          label: t('matters.name-on-invoice-local'),
                      },
                  ]
                : []),
            isSharedPaymentEnabled && (matter.payers?.length ?? 0) > 1
                ? {
                      content: <PayerShareList payers={matter.payers} />,
                      label: t('matters.payers.title'),
                      wide: true,
                  }
                : {
                      content: contact(matter.payerParty),
                      label: t('matters.payer'),
                  },
            {
                // The cell keeps the line the form's Customise button takes under the select, so the rows
                // below stay where they are when the card edits.
                content: hasCustomizeButton ? (
                    <div className="min-h-12">
                        <EllipsisTooltip>{matter.invoiceTemplate?.name || '-'}</EllipsisTooltip>
                    </div>
                ) : (
                    matter.invoiceTemplate?.name || '-'
                ),
                label: t('matters.invoice-template'),
            },
            {
                content: matter.invoiceSpecTemplate?.name || '-',
                label: t('matters.invoice-specification-template'),
            },
            {
                content: matter.isSpecIncludedInInvoice ? t('yes') : t('no'),
                label: t('invoices.general-tab.details.merge-invoice-and-spec'),
            },
            {
                content: matter.isSpecExcludedFromEmail ? t('yes') : t('no'),
                label: t('invoices.general-tab.details.exclude-spec-pdf-attachment-in-email'),
            },
            {
                content: <FormattedDate value={matter.nextInvoicingDate} />,
                label: t('matters.next-invoicing-date'),
            },
            {
                content:
                    matter.paymentNotificationReceivers && matter.paymentNotificationReceivers.length > 0
                        ? getLinks(
                              'employee',
                              matter.paymentNotificationReceivers.map((item) => item.employee)
                          )
                        : '-',
                label: t('contacts.billing.invoice-payment-notification-to'),
                chips: getLinkChips(
                    'employee',
                    (matter.paymentNotificationReceivers ?? []).map((item) => item.employee)
                ),
            },
            // The field is the opposite question, and the new design's row asks it as the field does.
            isNew
                ? { content: matter.isJointInvoiceDisabled ? t('yes') : t('no'), label: t('matters.no-joint-invoice') }
                : { content: matter.isJointInvoiceDisabled ? t('no') : t('yes'), label: t('matters.joint-invoicing') },
            {
                content:
                    getClassifierText(
                        classifiers['MATTER_BLOCKING_POLICY_TYPE'],
                        matter.matterBlockingPolicyTypeCode
                    ) || '-',
                label: t('matters.timecard-blocking-policy'),
            },
            {
                content: matter.isAmlBlockingExempt ? t('yes') : t('no'),
                label: t('matters.excluded-from-aml-blocking'),
            },
            {
                content: matter.blockingHistory || '-',
                label: t('contacts.blocking-history'),
                multiline: true,
                // Kept by the timecard blocking itself: no field edits it.
                readOnly: true,
            },
        ];
    }, [matter, classifiers, isLocalizedFieldsEnabled, isSharedPaymentEnabled, isNew, hasCustomizeButton]);

    const getAccountingData = useCallback(
        (): GridItemRow[] => [
            {
                content:
                    getClassifierText(classifiers['ACCOUNTING_REVENUE_TYPE'], matter.accountingRevenueTypeCode) || '-',
                label: t('matters.accounting-revenue-type'),
            },
        ],
        [matter, classifiers]
    );

    const pricingRows = withMissing(getPricingData(), missingFields);
    const billingRows = withMissing(getBillingData(), missingFields);
    return (
        <div className="new:gap-(--card-gap) grid grid-cols-1 gap-8 sm:grid-cols-2">
            <div>
                <MatterSection id="pricing" titleId="matters.pricing" matter={matter} rows={pricingRows}>
                    {(edit) => (
                        <GridItemList
                            isNew={isNew}
                            titleId="matters.pricing"
                            size={12}
                            data={pricingRows}
                            key="matter-pricing"
                            button={edit}
                        />
                    )}
                </MatterSection>
                {isAccountingRevenueTypeEnabled ? (
                    <MatterSection
                        id="accounting"
                        titleId="matters.accounting-details"
                        matter={matter}
                        className="new:mt-(--card-gap) mt-6"
                    >
                        {(edit) => (
                            <GridItemList
                                isNew={isNew}
                                titleId="matters.accounting-details"
                                className="new:mt-(--card-gap) mt-6"
                                size={12}
                                data={getAccountingData()}
                                key="matter-accounting"
                                button={edit}
                            />
                        )}
                    </MatterSection>
                ) : null}
            </div>
            <div>
                <MatterSection
                    id="invoice-spec"
                    titleId="matters.invoice-specification"
                    matter={matter}
                    rows={billingRows}
                >
                    {(edit) => (
                        <GridItemList
                            isNew={isNew}
                            titleId="matters.invoice-specification"
                            size={12}
                            data={billingRows}
                            key="matter-billing"
                            button={edit}
                        />
                    )}
                </MatterSection>
            </div>
        </div>
    );
};

export default PricingView;
